import React, { useState, useEffect } from 'react';
import api from '../services/api';
import SearchableSelect from './SearchableSelect';
import { useToast } from '../context/ToastContext';

interface Staff {
  id: number;
  name: string;
}

export interface Content {
  id?: number;
  title: string;
  description?: string;
  type: string;
  link: string;
  responsible?: number;
  responsible_name?: string;
  expected_completion_date?: string;
  created_at?: string;
}

interface ContentFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  contentToEdit?: Content | null;
}

const ContentForm: React.FC<ContentFormProps> = ({ onSuccess, onCancel, contentToEdit }) => {
  const { addToast } = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('Booklet');
  const [link, setLink] = useState('');
  const [responsible, setResponsible] = useState<number | ''>('');
  const [expectedCompletionDate, setExpectedCompletionDate] = useState('');
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await api.get('staff/?active_only=true');
        setAllStaff(response.data);
      } catch (error) {
        console.error('Failed to fetch staff', error);
      }
    };
    fetchStaff();
  }, []);

  useEffect(() => {
    if (contentToEdit) {
      setTitle(contentToEdit.title);
      setDescription(contentToEdit.description || '');
      setType(contentToEdit.type);
      setLink(contentToEdit.link);
      setResponsible(contentToEdit.responsible || '');
      setExpectedCompletionDate(contentToEdit.expected_completion_date || '');
    } else {
      setTitle('');
      setDescription('');
      setType('Booklet');
      setLink('');
      setResponsible('');
      setExpectedCompletionDate('');
    }
  }, [contentToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const contentData = { 
      title, 
      description,
      type, 
      link,
      responsible: responsible || null,
      expected_completion_date: expectedCompletionDate || null
    };

    try {
      if (contentToEdit && contentToEdit.id) {
        await api.patch(`contents/${contentToEdit.id}/`, contentData);
        addToast('Conteúdo atualizado com sucesso!', 'success');
      } else {
        await api.post('contents/', contentData);
        addToast('Novo material cadastrado!', 'success');
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save content', error);
      addToast('Erro ao salvar material educativo.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ title, icon }: { title: string, icon: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', marginTop: '10px' }}>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <h4 style={{ margin: 0, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-blue)' }}>{title}</h4>
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '25px', borderBottom: '1px solid var(--border-color)', paddingBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{contentToEdit ? '📚 Editar Conteúdo' : '✨ Produzir Novo Conteúdo'}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <SectionTitle title="Definição do Material" icon="📝" />
        <div className="form-group">
          <label>Título do Conteúdo</label>
          <input 
            type="text" 
            value={title} 
            onChange={(e) => setTitle(e.target.value)} 
            placeholder="Ex: Cartilha de Boas Práticas Digitais"
            required 
          />
        </div>
        
        <div className="form-group">
          <label>Resumo / Ideia Principal</label>
          <textarea 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            placeholder="Descreva o objetivo deste conteúdo e o público-alvo..."
            style={{ height: '100px' }}
          />
        </div>

        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Classificação e Mídia" icon="🎬" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div className="form-group">
            <label>Tipo de Material</label>
            <select value={type} onChange={(e) => setType(e.target.value)} required>
              <option value="Booklet">📖 Cartilha / E-book</option>
              <option value="Text">📄 Artigo / Texto</option>
              <option value="Video">🎥 Vídeo / Aula</option>
              <option value="Post">📱 Post Social Media</option>
            </select>
          </div>
          <div className="form-group">
            <label>Previsão de Conclusão</label>
            <input type="date" value={expectedCompletionDate} onChange={(e) => setExpectedCompletionDate(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Link de Acesso (Drive, YouTube, etc)</label>
          <input 
            type="url" 
            value={link} 
            onChange={(e) => setLink(e.target.value)} 
            placeholder="https://exemplo.com/seu-link"
          />
        </div>

        <div style={{ marginTop: '10px' }}>
          <SearchableSelect 
            label="Colaborador Responsável"
            options={allStaff}
            value={responsible}
            onChange={setResponsible}
            placeholder="Quem está produzindo este material?"
          />
        </div>

        <div style={{ 
          marginTop: '40px', 
          paddingTop: '20px', 
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button type="button" onClick={onCancel} className="btn btn-secondary">Cancelar</button>
          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar Conteúdo'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContentForm;
